// Mobile Checkout Success Page
// Order confirmation after successful checkout (Mobile)

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Order, OrderItem } from '@/types';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import styles from './MobileCheckoutSuccessPage.module.css';

export default function MobileCheckoutSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    const stateOrder = location.state as { order?: Order } | null;
    if (stateOrder?.order) {
      setOrder(stateOrder.order);
    } else {
      navigate('/orders', { replace: true });
    }
  }, [location, navigate]);

  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!order) {
    return (
      <div className={styles.successPage}>
        <div className={styles.loading}>載入中...</div>
      </div>
    );
  }


  return (
    <div className={styles.successPage}>
      <MobileHeader
        title="訂單成功"
        showBack={false}
      />
      {/* Success Icon */}
      <div className={styles.successIcon}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>

      {/* Success Message */}
      <h1 className={styles.title}>訂單建立成功！</h1>

      {/* Order Info */}
      <div className={styles.orderCard}>
        <div className={styles.orderNumber}>{order.orderNumber}</div>
        <div className={styles.orderMeta}>
          <span className={styles.orderDate}>{formatDate(order.createdAt)}</span>
          <span className={styles.orderAmount}>{formatPrice(order.total)}</span>
        </div>
      </div>

      {/* Order Items */}
      <div className={styles.itemsSection}>
        <div className={styles.itemsList}>
          {order.items.map((item: OrderItem) => (
            <div key={item.id} className={styles.item}>
              <div className={styles.itemInfo}>
                <span className={styles.itemTitle}>{item.productSnapshot.title}</span>
                <span className={styles.itemMeta}>×{item.quantity}</span>
              </div>
              <span className={styles.itemPrice}>{formatPrice(item.subtotal)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recipient Info */}
      <div className={styles.recipientCard}>
        <h2 className={styles.cardTitle}>收件人資訊</h2>
        <p className={styles.infoLine}>{order.recipientName}</p>
        <p className={styles.infoLine}>{order.recipientPhone}</p>
        <p className={styles.infoLine}>{order.recipientAddress}</p>
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button
          onClick={() => navigate(`/orders/${order.id}`)}
          className={styles.primaryButton}
        >
          查看訂單詳情
        </button>
        <button
          onClick={() => navigate('/products')}
          className={styles.secondaryButton}
        >
          繼續購物
        </button>
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
